import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { Invitees } from '@atlassian/invite-prompt/types';
import { InvitePromptFlag } from '@atlassian/invite-prompt/invite-prompt-flag/main';
import { mapInviteResult } from '@atlassian/expansion-services/services';
import { AnyAri } from '@atlassian/ari';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import {
	incrementDismissalCount,
	setUserOptOutTrue,
	shouldShowOptOutOption,
} from '../services/opt-out.ts';
import { useInvite } from '../services/use-invite/index.tsx';

import { useInviteSuccessFlag } from './useInviteSuccessFlag.tsx';
import { useInviteErrorFlag } from './useInviteErrorFlag.tsx';

const PRODUCT = 'jira-software';
const MAX_NUMBER_OF_INVITEES = 10;

export const useInvitePromptFlag = (
	flags?: FlagsStateContainer,
): ((
	invitees: Invitees,
	targetTitle: string,
	continueUrl: string,
	jiraCloudId: string,
	pageVersion: number,
) => void) => {
	const invite = useInvite();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showSuccessFlag = useInviteSuccessFlag(flags);
	const showErrorFlag = useInviteErrorFlag(flags);
	return useCallback(
		(
			invitees: Invitees,
			targetTitle: string,
			continueUrl: string,
			jiraCloudId: string,
			pageVersion: number,
		) => {
			const attributes = {
				invitees: invitees.length,
				pageVersion,
			};

			const onDismissFlag = (attributes: any) => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'dismissFlag',
						source: 'inviteOnLinkInsert',
						attributes,
					},
				}).fire();
				incrementDismissalCount();
			};

			const onOptOutFlag = (id: string | number, attributes: any, flags?: FlagsStateContainer) => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'optOutFlag',
						source: 'inviteOnLinkInsert',
						attributes,
					},
				}).fire();
				flags?.hideFlag(id);
				setUserOptOutTrue();
			};

			flags?.showFlag({
				customComponent: (props) => (
					<InvitePromptFlag
						{...props}
						id={props.id}
						invitees={invitees.slice(0, MAX_NUMBER_OF_INVITEES)}
						targetTitle={targetTitle}
						targetProduct={PRODUCT}
						onDismiss={() => {
							onDismissFlag(attributes);
						}}
						onOptOut={
							shouldShowOptOutOption()
								? () => {
										onOptOutFlag(props.id, attributes, flags);
									}
								: undefined
						}
						onSubmit={async () => {
							const response = await invite({
								continueUrl,
								users: invitees.map(({ id }) => ({ id })),
								touchpoint: 'Prompt invite on link insert',
								integration: 'invitePromptOnLink',
								resources: [
									AnyAri.create({
										resourceOwner: PRODUCT,
										resourceType: 'site',
										resourceId: jiraCloudId,
									}),
								],
							});
							flags.hideFlag(props.id);
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'addPeople',
									source: 'inviteOnLinkInsert',
									attributes,
								},
							}).fire();

							const { error, failure } = mapInviteResult(response);
							if (error.length > 0 || failure) {
								showErrorFlag();
								return;
							}
							showSuccessFlag(invitees);
						}}
					/>
				),
			});

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'shown',
					actionSubject: 'flag',
					actionSubjectId: 'invitePromptFlag',
					source: 'inviteOnLinkInsert',
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, flags, invite, showErrorFlag, showSuccessFlag],
	);
};
